/** French hub slug → English hub slug (URL segment under /en/) */
export const HUB_SLUG_MAP_FR_TO_EN = {
  webrtc: 'webrtc-video-infrastructure',
  'plateforme-video': 'business-video-platform',
  'api-video': 'video-api',
  'hebergement-france': 'france-hosted-video-platform',
  rgpd: 'gdpr-video-compliance',
  'sfu-webrtc': 'sfu-webrtc-infrastructure',
  'integration-video': 'custom-video-integration',
  'alternative-zoom-france': 'zoom-alternative-eu',
  'video-embarquee': 'embedded-video-platform',
  'open-source': 'open-source-video-infrastructure',
  'visioconference-souveraine': 'sovereign-video-conferencing',
  'stun-turn': 'stun-turn-webrtc',
  'latence-video': 'low-latency-video-platform',
  'scalabilite-webrtc': 'scalable-webrtc-infrastructure',
  'cloud-souverain-video': 'sovereign-video-cloud',
  'architecture-video': 'video-architecture',
  'communication-temps-reel': 'real-time-communication-platform',
};

export const HUB_SLUG_MAP_EN_TO_FR = Object.fromEntries(
  Object.entries(HUB_SLUG_MAP_FR_TO_EN).map(([fr, en]) => [en, fr]),
);

/** French solutions slug → English solutions slug (under /en/solutions/) */
export const SOLUTIONS_SLUG_MAP_FR_TO_EN = {
  'assistance-video-client': 'customer-video-support',
  'visioconference-professionnelle': 'professional-video-conferencing',
  'video-sur-mesure-webrtc': 'custom-webrtc-video',
  'rendez-vous-video': 'video-appointments',
  'assistance-qr-code': 'qr-code-video-support',
  'streaming-live': 'live-streaming',
};

export const SOLUTIONS_SLUG_MAP_EN_TO_FR = Object.fromEntries(
  Object.entries(SOLUTIONS_SLUG_MAP_FR_TO_EN).map(([fr, en]) => [en, fr]),
);

/** French comparatifs slug → English comparisons slug (under /en/comparisons/) */
export const COMPARATIFS_SLUG_MAP_FR_TO_EN = {
  'alternative-zoom-heberge-france': 'zoom-alternative-gdpr',
  'alternative-teams-rgpd': 'microsoft-teams-gdpr-alternative',
  'visio-sans-installation': 'browser-based-video-conferencing',
};

export const COMPARATIFS_SLUG_MAP_EN_TO_FR = Object.fromEntries(
  Object.entries(COMPARATIFS_SLUG_MAP_FR_TO_EN).map(([fr, en]) => [en, fr]),
);

export function getLocaleFromPathname(pathname) {
  return pathname.startsWith('/en') ? 'en' : 'fr';
}

function normalizePath(path) {
  if (!path || path === '/') return '/';
  return path.endsWith('/') ? path : `${path}/`;
}

/** Sections with a real index route (see App.js). */
export const SECTION_INDEX_PATHS = {
  articles: '/articles/',
  solutions: '/solutions/',
  'solutions-en': '/en/solutions/',
  secteurs: '/secteurs/',
  comparatifs: '/comparatifs/',
  'comparatifs-en': '/en/comparisons/',
};

const NON_HUB_ROOT_SEGMENTS = [
  'en',
  'articles',
  'devis',
  'solutions',
  'secteurs',
  'comparatifs',
  'a-propos',
  'securite-rgpd',
  'references',
];

function isFrRootHubPath(path) {
  const match = path.match(/^\/([^/]+)\/$/);
  if (!match) return false;
  return !NON_HUB_ROOT_SEGMENTS.includes(match[1]);
}

function getHubAlternatePaths(path) {
  const enHubMatch = path.match(/^\/en\/([^/]+)\/$/);
  if (enHubMatch) {
    const enSlug = enHubMatch[1];
    const frSlug = HUB_SLUG_MAP_EN_TO_FR[enSlug];
    if (!frSlug) return null;
    return { fr: `/${frSlug}/`, en: `/en/${enSlug}/` };
  }

  if (isFrRootHubPath(path)) {
    const frSlug = path.slice(1, -1);
    const enSlug = HUB_SLUG_MAP_FR_TO_EN[frSlug];
    if (!enSlug) return null;
    return { fr: `/${frSlug}/`, en: `/en/${enSlug}/` };
  }

  return null;
}

function getSolutionsAlternatePaths(path) {
  const enMatch = path.match(/^\/en\/solutions\/([^/]+)\/$/);
  if (enMatch) {
    const enSlug = enMatch[1];
    const frSlug = SOLUTIONS_SLUG_MAP_EN_TO_FR[enSlug];
    if (!frSlug) return null;
    return { fr: `/solutions/${frSlug}/`, en: `/en/solutions/${enSlug}/` };
  }

  const frMatch = path.match(/^\/solutions\/([^/]+)\/$/);
  if (frMatch) {
    const frSlug = frMatch[1];
    const enSlug = SOLUTIONS_SLUG_MAP_FR_TO_EN[frSlug];
    if (!enSlug) return null;
    return { fr: `/solutions/${frSlug}/`, en: `/en/solutions/${enSlug}/` };
  }

  return null;
}

function getComparatifsAlternatePaths(path) {
  const enMatch = path.match(/^\/en\/comparisons\/([^/]+)\/$/);
  if (enMatch) {
    const enSlug = enMatch[1];
    const frSlug = COMPARATIFS_SLUG_MAP_EN_TO_FR[enSlug];
    if (!frSlug) return null;
    return { fr: `/comparatifs/${frSlug}/`, en: `/en/comparisons/${enSlug}/` };
  }

  const frMatch = path.match(/^\/comparatifs\/([^/]+)\/$/);
  if (frMatch) {
    const frSlug = frMatch[1];
    const enSlug = COMPARATIFS_SLUG_MAP_FR_TO_EN[frSlug];
    if (!enSlug) return null;
    return { fr: `/comparatifs/${frSlug}/`, en: `/en/comparisons/${enSlug}/` };
  }

  return null;
}

function getContentAlternatePaths(path) {
  return (
    getHubAlternatePaths(path)
    || getSolutionsAlternatePaths(path)
    || getComparatifsAlternatePaths(path)
  );
}

export function getLocalizedPath(pathname, targetLocale) {
  const path = normalizePath(pathname);

  if (path === '/' || path === '/en/') {
    return targetLocale === 'en' ? '/en/' : '/';
  }

  if (path === '/devis/' || path === '/en/quote/') {
    return targetLocale === 'en' ? '/en/quote/' : '/devis/';
  }

  if (path === '/solutions/' || path === '/en/solutions/') {
    return targetLocale === 'en' ? '/en/solutions/' : '/solutions/';
  }

  if (path === '/comparatifs/' || path === '/en/comparisons/') {
    return targetLocale === 'en' ? '/en/comparisons/' : '/comparatifs/';
  }

  const contentAlternate = getContentAlternatePaths(path);
  if (contentAlternate) {
    return targetLocale === 'en' ? contentAlternate.en : contentAlternate.fr;
  }

  const enHubMatch = path.match(/^\/en\/([^/]+)\/$/);
  if (enHubMatch) {
    return `/en/${enHubMatch[1]}/`;
  }

  if (isFrRootHubPath(path)) {
    return targetLocale === 'en' ? '/en/' : path;
  }

  if (path.startsWith('/en/')) {
    return path;
  }

  return targetLocale === 'en' ? '/en/' : path;
}

/**
 * hreflang alternates: only declare FR/EN pairs when content is truly equivalent.
 * Pages without a translation keep a self-referencing canonical locale + x-default.
 */
export function getHrefLangPaths(pathname) {
  const path = normalizePath(pathname);

  if (path === '/' || path === '/en/') {
    return {
      fr: '/',
      en: '/en/',
      xDefault: '/',
      alternateLocales: ['fr', 'en'],
    };
  }

  if (path === '/devis/' || path === '/en/quote/') {
    return {
      fr: '/devis/',
      en: '/en/quote/',
      xDefault: '/devis/',
      alternateLocales: ['fr', 'en'],
    };
  }

  if (path === '/solutions/' || path === '/en/solutions/') {
    return {
      fr: '/solutions/',
      en: '/en/solutions/',
      xDefault: '/solutions/',
      alternateLocales: ['fr', 'en'],
    };
  }

  if (path === '/comparatifs/' || path === '/en/comparisons/') {
    return {
      fr: '/comparatifs/',
      en: '/en/comparisons/',
      xDefault: '/comparatifs/',
      alternateLocales: ['fr', 'en'],
    };
  }

  const contentAlternate = getContentAlternatePaths(path);
  if (contentAlternate) {
    return {
      fr: contentAlternate.fr,
      en: contentAlternate.en,
      xDefault: contentAlternate.fr,
      alternateLocales: ['fr', 'en'],
    };
  }

  const enOnlyMatch = path.match(/^\/en\/(solutions|comparisons)\/([^/]+)\/$/)
    || path.match(/^\/en\/([^/]+)\/$/);
  if (enOnlyMatch && path.startsWith('/en/')) {
    return {
      fr: null,
      en: path,
      xDefault: path,
      alternateLocales: ['en'],
    };
  }

  if (isFrRootHubPath(path)) {
    return {
      fr: path,
      en: null,
      xDefault: path,
      alternateLocales: ['fr'],
    };
  }

  return {
    fr: path,
    en: null,
    xDefault: path,
    alternateLocales: ['fr'],
  };
}

export function getSectionIndexPath(section) {
  return SECTION_INDEX_PATHS[section] || null;
}

/** Map content section to i18n index key prefix (e.g. solutionsIndex). */
export function getSectionI18nKey(section) {
  if (section === 'solutions-en') return 'solutionsIndex';
  if (section === 'comparatifs-en') return 'comparatifsIndex';
  return `${section}Index`;
}

export function enHubSlugToOgSlug(enSlug) {
  return HUB_SLUG_MAP_EN_TO_FR[enSlug] || enSlug;
}

export const HUB_EXPLORER_LINKS = {
  fr: [
    { to: '/webrtc/', iconKey: 'webrtc', labelKey: 'webrtc', descKey: 'webrtcDesc' },
    { to: '/plateforme-video/', iconKey: 'platform', labelKey: 'platform', descKey: 'platformDesc' },
    { to: '/hebergement-france/', iconKey: 'hosting', labelKey: 'hosting', descKey: 'hostingDesc' },
    { to: '/rgpd/', iconKey: 'rgpd', labelKey: 'rgpd', descKey: 'rgpdDesc' },
    { to: '/api-video/', iconKey: 'api', labelKey: 'api', descKey: 'apiDesc' },
    { to: '/integration-video/', iconKey: 'integration', labelKey: 'integration', descKey: 'integrationDesc' },
    { to: '/alternative-zoom-france/', iconKey: 'zoomAlt', labelKey: 'zoomAlt', descKey: 'zoomAltDesc' },
    { to: '/sfu-webrtc/', iconKey: 'sfu', labelKey: 'sfu', descKey: 'sfuDesc' },
    { to: '/stun-turn/', iconKey: 'stunTurn', labelKey: 'stunTurn', descKey: 'stunTurnDesc' },
    { to: '/visioconference-souveraine/', iconKey: 'sovereign', labelKey: 'sovereign', descKey: 'sovereignDesc' },
    { to: '/open-source/', iconKey: 'openSource', labelKey: 'openSource', descKey: 'openSourceDesc' },
    { to: '/cloud-souverain-video/', iconKey: 'cloud', labelKey: 'cloud', descKey: 'cloudDesc' },
    { to: '/latence-video/', iconKey: 'latency', labelKey: 'latency', descKey: 'latencyDesc' },
  ],
  en: [
    { to: '/en/webrtc-video-infrastructure/', iconKey: 'webrtc', labelKey: 'webrtc', descKey: 'webrtcDesc' },
    { to: '/en/business-video-platform/', iconKey: 'platform', labelKey: 'platform', descKey: 'platformDesc' },
    { to: '/en/france-hosted-video-platform/', iconKey: 'hosting', labelKey: 'hosting', descKey: 'hostingDesc' },
    { to: '/en/gdpr-video-compliance/', iconKey: 'rgpd', labelKey: 'rgpd', descKey: 'rgpdDesc' },
    { to: '/en/video-api/', iconKey: 'api', labelKey: 'api', descKey: 'apiDesc' },
    { to: '/en/custom-video-integration/', iconKey: 'integration', labelKey: 'integration', descKey: 'integrationDesc' },
    { to: '/en/zoom-alternative-eu/', iconKey: 'zoomAlt', labelKey: 'zoomAlt', descKey: 'zoomAltDesc' },
    { to: '/en/sfu-webrtc-infrastructure/', iconKey: 'sfu', labelKey: 'sfu', descKey: 'sfuDesc' },
    { to: '/en/stun-turn-webrtc/', iconKey: 'stunTurn', labelKey: 'stunTurn', descKey: 'stunTurnDesc' },
    { to: '/en/sovereign-video-conferencing/', iconKey: 'sovereign', labelKey: 'sovereign', descKey: 'sovereignDesc' },
    { to: '/en/open-source-video-infrastructure/', iconKey: 'openSource', labelKey: 'openSource', descKey: 'openSourceDesc' },
    { to: '/en/sovereign-video-cloud/', iconKey: 'cloud', labelKey: 'cloud', descKey: 'cloudDesc' },
    { to: '/en/low-latency-video-platform/', iconKey: 'latency', labelKey: 'latency', descKey: 'latencyDesc' },
  ],
};

export const FOOTER_HUB_LINKS = {
  fr: [
    { to: '/webrtc/', label: 'WebRTC' },
    { to: '/hebergement-france/', label: 'Hébergement France' },
    { to: '/rgpd/', label: 'RGPD vidéo' },
    { to: '/api-video/', label: 'API vidéo' },
    { to: '/plateforme-video/', label: 'Plateforme vidéo' },
    { to: '/alternative-zoom-france/', label: 'Alternative Zoom France' },
    { to: '/sfu-webrtc/', label: 'SFU WebRTC' },
    { to: '/stun-turn/', label: 'STUN / TURN' },
    { to: '/open-source/', label: 'Open source' },
    { to: '/visioconference-souveraine/', label: 'Visio souveraine' },
  ],
  en: [
    { to: '/en/webrtc-video-infrastructure/', label: 'WebRTC' },
    { to: '/en/france-hosted-video-platform/', label: 'France hosting' },
    { to: '/en/gdpr-video-compliance/', label: 'GDPR video' },
    { to: '/en/video-api/', label: 'Video API' },
    { to: '/en/business-video-platform/', label: 'Business video platform' },
    { to: '/en/zoom-alternative-eu/', label: 'Zoom alternative EU' },
    { to: '/en/sfu-webrtc-infrastructure/', label: 'SFU WebRTC' },
    { to: '/en/stun-turn-webrtc/', label: 'STUN / TURN' },
    { to: '/en/open-source-video-infrastructure/', label: 'Open source' },
    { to: '/en/low-latency-video-platform/', label: 'WebRTC latency' },
    { to: '/en/sovereign-video-conferencing/', label: 'Sovereign video' },
  ],
};
